import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { assetSrc } from "@/lib/api";
import { track, withUtm } from "@/lib/track";
import { MessageCircle, Phone, Mail, BadgeCheck, MapPin, TrendingUp, Home, ArrowRight, Layers } from "lucide-react";
import ProjectCard, { formatPrice } from "@/components/ProjectCard";
import QualifyDialog from "@/components/QualifyDialog";

export default function AgentMicrosite() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [variant, setVariant] = useState("All");

  useEffect(() => {
    api.get(`/agent/${slug}`).then(({ data }) => setData(data)).catch(() => setError("Agent not found."));
  }, [slug]);

  // Legacy-slug 301-style redirect: if the URL used an old alias, replace it with the canonical.
  useEffect(() => {
    if (data?.canonical_slug && data.canonical_slug !== slug) {
      navigate(`/agent/${data.canonical_slug}`, { replace: true });
    }
  }, [data, slug, navigate]);

  useEffect(() => {
    if (slug) track({ event_type: "microsite_view", agent_slug: slug, source: "microsite" });
  }, [slug]);

  const listings = data?.listings || [];
  const visibleListings = useMemo(() => {
    if (variant === "All") return listings;
    return listings.filter((l) => (l.unit_variant || "") === variant);
  }, [listings, variant]);

  if (error) return <div className="min-h-screen bg-sg-paper flex items-center justify-center text-sg-mute">{error}</div>;
  if (!data) return <div className="min-h-screen bg-sg-paper flex items-center justify-center text-sg-mute animate-pulse">Loading branded portal…</div>;

  const { agent, projects, market_insight, available_variants, coverage_localities, locality_trends } = data;
  const variants = available_variants || [];
  const extras = agent.additional_localities || [];
  const trends = locality_trends || {};
  const coverage = coverage_localities || [];
  const waNum = (agent.phone || "+919999999999").replace(/[^0-9]/g, "");
  const waMessage = encodeURIComponent(`Hi ${agent.name}, I came in via your INPropPortal page.`);
  const waHref = withUtm(`https://wa.me/${waNum}?text=${waMessage}`, { agent_slug: agent.slug });
  const onWaClick = () => track({ event_type: "wa_click", agent_slug: agent.slug, source: "microsite" });
  const onCallClick = () => track({ event_type: "call_click", agent_slug: agent.slug, source: "microsite" });

  return (
    <div className="bg-sg-paper">
      {/* AgentNav now provided by AgentLayout — no local header */}

      {/* AGENT HEADER CARD — SG-style two-column with avatar + identity */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-10">
        <div className="sg-card-dark relative overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center opacity-15"
               style={{ backgroundImage: "url(https://images.pexels.com/photos/21835254/pexels-photo-21835254.jpeg)" }} />
          <div className="relative grid grid-cols-1 md:grid-cols-[180px_1fr_auto] gap-8 items-center">
            <div>
              <img
                src={agent.picture || "https://images.pexels.com/photos/7580994/pexels-photo-7580994.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=320&w=320"}
                alt={agent.name}
                className="w-40 h-40 rounded-full object-cover border-2 border-sg-gold/60"
                data-testid="agent-headshot"
              />
            </div>
            <div>
              <div className="overline text-sg-gold mb-2">{agent.micro_market?.toUpperCase()} SPECIALIST</div>
              <h1 className="font-display text-4xl lg:text-5xl leading-[1.05]" data-testid="agent-name">
                {agent.name}
              </h1>
              <p className="mt-3 text-white/75 max-w-xl">
                {agent.bio || `Independent property consultant operating across ${agent.primary_locality} and surrounding micro-markets.`}
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-2">
                <span className="chip chip-gold inline-flex items-center gap-1" data-testid="rera-registration-badge">
                  <BadgeCheck className="w-3.5 h-3.5" /> MahaCRITI Reg No: {agent.maharera_number}
                </span>
                <span className="chip" data-testid="primary-locality-chip">
                  <MapPin className="w-3 h-3 mr-1" /> Primary · {agent.primary_locality}
                </span>
                {extras.map((loc) => (
                  <span key={loc} className="chip" data-testid={`additional-locality-chip-${loc.toLowerCase().replace(/\s+/g, "-")}`}>
                    <MapPin className="w-3 h-3 mr-1 opacity-70" /> {loc}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex md:flex-col gap-2 md:items-end">
              <a href={waHref} target="_blank" rel="noreferrer" onClick={onWaClick} className="pill-solid inline-flex items-center gap-2" data-testid="agent-header-wa">
                <MessageCircle className="w-4 h-4" /> WhatsApp Agent
              </a>
              {agent.phone && (
                <a href={`tel:${agent.phone}`} onClick={onCallClick} className="pill-outline text-white border-white/40 hover:bg-white hover:text-sg-navy inline-flex items-center gap-2" data-testid="agent-header-call">
                  <Phone className="w-4 h-4" /> Call
                </a>
              )}
              <a href="mailto:" className="pill-outline text-white border-white/40 hover:bg-white hover:text-sg-navy inline-flex items-center gap-2" data-testid="agent-header-email">
                <Mail className="w-4 h-4" /> Email
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Strip */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-6">
        <div className="bg-white border border-sg-line rounded-full text-center py-3 overline text-sg-navy/70">
          Live inventory across {coverage.length > 1 ? `${coverage.length} micro-markets` : agent.micro_market} · MahaCRITI-verified agent
        </div>
      </div>

      {/* MARKET INSIGHT — primary locality */}
      {market_insight && (
        <section className="max-w-7xl mx-auto px-6 lg:px-8 mt-12">
          <div className="sg-card-cream flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-testid="market-insight-card">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-white border border-sg-line flex items-center justify-center"><TrendingUp className="w-5 h-5 text-sg-gold-deep" /></div>
              <div>
                <div className="overline text-sg-gold-deep">MARKET INSIGHT · {agent.primary_locality?.toUpperCase()}</div>
                <p className="mt-1 text-sg-navy max-w-2xl">{market_insight.summary}</p>
              </div>
            </div>
            <div className="rupee text-5xl text-sg-navy">{market_insight.yoy}<span className="text-base font-sans text-sg-mute ml-2">YoY</span></div>
          </div>
        </section>
      )}

      {/* MARKET TRENDS — every covered micro-market */}
      {Object.keys(trends).length > 1 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-8 mt-8" data-testid="locality-trends-grid">
          <div className="overline text-sg-gold-deep mb-3">MICRO-MARKET PULSE</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {coverage
              .filter((loc) => trends[loc])
              .map((loc) => (
                <div
                  key={loc}
                  className={`bg-white border rounded-2xl p-4 ${loc === agent.primary_locality ? "border-sg-gold/60" : "border-sg-line"}`}
                  data-testid={`locality-trend-${loc.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="overline text-sg-navy/70 text-[10px]">{loc}</div>
                    <div className="rupee text-base text-sg-navy">{trends[loc].yoy}</div>
                  </div>
                  <p className="text-xs text-sg-mute mt-1 line-clamp-2">{trends[loc].summary}</p>
                </div>
              ))}
          </div>
        </section>
      )}

      {/* FEATURED PROJECTS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="overline text-sg-gold-deep mb-3">FEATURED · MASTER PROJECTS</div>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
          <h2 className="font-display text-4xl lg:text-5xl text-sg-navy max-w-2xl">
            Curated launches in <span className="font-serif-italic text-sg-gold-deep">{coverage.length > 1 ? "Pune East & West" : agent.micro_market}.</span>
          </h2>
          <Link to={`/agent/${slug}/buy`} className="pill-outline inline-flex items-center gap-2">
            See all projects <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.slice(0, 9).map((p) => (
            <ProjectCard
              key={p.project_id}
              project={p}
              tag="NEW LAUNCH"
              inquireHref={withUtm(`https://wa.me/${waNum}?text=${encodeURIComponent(`Hi ${agent.name}, I'm interested in ${p.project_name} (${p.locality_tag}).`)}`, { agent_slug: agent.slug, project_id: p.project_id })}
              detailHref={`/agent/${agent.slug}/project/${p.project_id}`}
              onInquire={() => track({ event_type: "wa_click", agent_slug: agent.slug, project_id: p.project_id, source: "microsite" })}
              testIdPrefix="microsite-project"
            />
          ))}
        </div>
      </section>

      {/* PUBLISHED LISTINGS */}
      {listings.length > 0 && (
        <section className="bg-white border-y border-sg-line py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
              <div>
                <div className="overline text-sg-gold-deep mb-3">RESALE · AGENT INVENTORY</div>
                <h2 className="font-display text-4xl text-sg-navy">
                  Active inventory <span className="text-sg-mute">({visibleListings.length}{variant !== "All" ? ` of ${listings.length}` : ""})</span>
                </h2>
              </div>
              {variants.length > 0 && (
                <div className="flex items-center gap-2 bg-sg-cream-soft border border-sg-line rounded-full px-1 py-1" data-testid="microsite-variant-filter">
                  <span className="text-xs text-sg-mute pl-3 pr-1 flex items-center gap-1"><Layers className="w-3.5 h-3.5" /> Variant</span>
                  <button
                    onClick={() => setVariant("All")}
                    className={`text-xs px-3 py-1.5 rounded-full transition-colors ${variant === "All" ? "bg-sg-navy text-white" : "text-sg-navy hover:bg-white"}`}
                    data-testid="microsite-variant-pill-all"
                  >All</button>
                  {variants.map((v) => (
                    <button
                      key={v}
                      onClick={() => setVariant(v)}
                      className={`text-xs px-3 py-1.5 rounded-full transition-colors ${variant === v ? "bg-sg-navy text-white" : "text-sg-navy hover:bg-white"}`}
                      data-testid={`microsite-variant-pill-${v.toLowerCase().replace(/\s+/g, "-")}`}
                    >{v}</button>
                  ))}
                </div>
              )}
            </div>
            {visibleListings.length === 0 ? (
              <div className="text-sm text-sg-mute py-12 text-center border border-dashed border-sg-line rounded-3xl" data-testid="microsite-no-variant-results">
                No <strong className="text-sg-navy">{variant}</strong> units currently listed. <button onClick={() => setVariant("All")} className="underline ml-1">Show all</button>.
              </div>
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-2">
              {visibleListings.map((l) => (
                <div key={l.listing_id} className="sg-card p-0 overflow-hidden" data-testid={`microsite-listing-${l.listing_id}`}>
                  {l.image_urls && l.image_urls.length > 0 ? (
                    <div className="aspect-[16/10] bg-sg-cream-soft overflow-hidden">
                      <img
                        src={assetSrc(l.image_urls[0])}
                        alt={`${l.bhk} ${l.property_type} in ${l.locality}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        data-testid={`microsite-listing-image-${l.listing_id}`}
                      />
                    </div>
                  ) : (
                    <div className="aspect-[16/10] bg-gradient-to-br from-sg-navy via-sg-navy-700 to-sg-navy-deep flex items-center justify-center">
                      <Home className="w-10 h-10 text-sg-gold/40" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="chip chip-dark">RESALE</span>
                      {l.unit_variant && (
                        <span className="chip chip-gold" data-testid={`microsite-variant-chip-${l.listing_id}`}>
                          <Layers className="w-3 h-3 mr-1" /> {l.unit_variant}
                        </span>
                      )}
                    </div>
                    <h3 className="font-display text-2xl mt-3 text-sg-navy">{l.bhk} {l.property_type}</h3>
                    <p className="text-sm text-sg-mute mt-1">{l.locality}</p>
                    {l.carpet_area_sqft && (
                      <p className="text-xs text-sg-mute mt-1 font-mono">{l.carpet_area_sqft} sq ft carpet</p>
                    )}
                    {l.notes && <p className="text-sm text-sg-mute mt-3 line-clamp-3">{l.notes}</p>}
                    <div className="mt-5 flex items-end justify-between border-t border-dashed border-sg-line pt-4">
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-sg-mute">Asking</div>
                        <div className="rupee text-3xl text-sg-navy mt-0.5">{formatPrice(l.price_lakhs)}</div>
                      </div>
                      <QualifyDialog listingId={l.listing_id} agentName={agent.name} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>
        </section>
      )}

      <footer className="py-10 border-t border-sg-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-wrap items-center justify-between gap-3 text-xs text-sg-mute">
          <span>© 2026 INPropPortal · MahaCRITI-aligned</span>
          <span className="font-mono">{agent.name} · MahaCRITI Reg No: {agent.maharera_number}</span>
        </div>
      </footer>
    </div>
  );
}
